import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const fmt = (v) => (v === null || v === undefined ? "—" : Array.isArray(v) ? (v.length ? v.join(", ") : "nessuno") : String(v));

export const P05Report = () => {
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/p05/report`, { timeout: 120000 }).then((r) => { setRep(r.data); setErr(null); })
      .catch((e) => setErr(e?.response?.data?.detail || "Report non disponibile"));
  };
  useEffect(load, []);

  if (err) return <p className="text-xs text-amber-800" data-testid="p05-report-blocked">{err}</p>;
  if (!rep) return <p className="text-xs">Caricamento report…</p>;

  const finalize = async () => {
    setBusy(true);
    try { await api.post(`/p05/finalize`); toast.success("Report congelato (REPORT.json/md) e verdetto registrato in audit"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };

  const vColor = rep.verdict === "CANDIDATE_FOR_LARGER_BLIND_TEST" ? "bg-emerald-100 text-emerald-800"
    : rep.verdict === "PILOT_FAILED" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800";
  return (
    <div className="space-y-3" data-testid="p05-report">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold">Verdetto:</span>
        <Badge className={`text-[11px] ${vColor}`} data-testid="p05-verdict">{rep.verdict}</Badge>
        <span className="text-[11px] text-muted-foreground">{rep.classification} · {rep.n_evaluable}/{rep.n_cases} valutabili</span>
        <Button size="sm" variant="outline" className="h-6 text-[11px]" disabled={busy} onClick={finalize} data-testid="p05-finalize">Congela report + verdetto</Button>
      </div>
      <div className="border border-border p-2">
        <h4 className="text-xs font-semibold">Fail-fast</h4>
        {Object.entries(rep.fail_fast || {}).map(([k, v]) => (
          <div key={k} className="text-[11px]"><b>{k}</b>: {Array.isArray(v) ? (v.length ? <span className="text-red-800">{v.join(", ")}</span> : "OK") : (v ? <span className="text-red-800">SÌ</span> : "OK")}</div>
        ))}
      </div>
      <div className="border border-border p-2">
        <h4 className="text-xs font-semibold">Gate MVP preregistrato</h4>
        <table className="w-full text-[11px]">
          <thead><tr className="text-left text-muted-foreground"><th className="pr-2 py-1">Criterio</th><th className="pr-2">Valore</th><th className="pr-2">Target</th><th>Esito</th></tr></thead>
          <tbody>
            {Object.entries(rep.mvp_gate || {}).map(([k, v]) => (
              <tr key={k} className="border-t border-border" data-testid={`p05-gate-${k}`}>
                <td className="pr-2 py-0.5">{k}</td><td className="pr-2">{fmt(v.value)}</td><td className="pr-2">{fmt(v.target)}</td>
                <td>{v.ok ? "✔" : "✘"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border border-border p-2">
        <h4 className="text-xs font-semibold">TP/FP/FN feature (aggregate)</h4>
        <table className="w-full text-[11px]">
          <thead><tr className="text-left text-muted-foreground"><th className="pr-2 py-1">Modalità</th><th className="pr-2">TP</th><th className="pr-2">FP</th><th className="pr-2">FN</th><th className="pr-2">Precision</th><th>Recall</th></tr></thead>
          <tbody>
            {Object.entries(rep.tp_fp_fn || {}).map(([m, v]) => (
              <tr key={m} className="border-t border-border"><td className="pr-2 py-0.5 font-medium">{m}</td><td className="pr-2">{v.tp}</td><td className="pr-2">{v.fp}</td><td className="pr-2">{v.fn}</td><td className="pr-2">{fmt(v.precision)}</td><td>{fmt(v.recall)}</td></tr>
            ))}
          </tbody>
        </table>
        <p className="text-[11px] mt-1">Campi critici espliciti confermati auto: <b>{rep.critical_fields?.auto_confirmed}/{rep.critical_fields?.explicit_total}</b> ({fmt(rep.critical_fields?.pct)}%) · READY auto: {fmt(rep.reading_ready_auto)} · falsi READY: {fmt(rep.false_reading_ready)} · recuperi IA verificati: {rep.ai_recovered_verified_total} · UNSUPPORTED: {rep.unsupported_ai_claims_total} · AI_ONLY: {rep.ai_only_total} · conflitti: {rep.conflicts_total}</p>
        <p className="text-[11px]">Costo medio: ${fmt(rep.cost?.mean_usd)} · latenza P95: {fmt(rep.latency?.p95_s)}s</p>
      </div>
      <div className="border border-border p-2">
        <h4 className="text-xs font-semibold">Per caso (4 vie: parser / IA / ibrido / revisione)</h4>
        {(rep.cases || []).map((c) => (
          <div key={c.neutral_id} className="text-[11px] border-t border-border py-1" data-testid={`p05-case-row-${c.neutral_id}`}>
            {!c.evaluable ? (
              <span><b>{c.neutral_id}</b>: NON VALUTABILE — {c.reason}</span>
            ) : (
              <span><b>{c.neutral_id}</b> · gate {c.gate} · errori critici promossi: {fmt(c.critical_errors_promoted)} · sovrastime: {fmt(c.overestimates)} · sottostime: {fmt(c.underestimates)} · lavorazioni mancanti: {fmt(c.operations?.missing)} · recuperi IA: {fmt(c.ai_recovered_verified)} · ${c.cost_usd} · {c.elapsed_s}s · revisione {fmt(c.review?.review_seconds)}s</span>
            )}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-amber-800">{rep.nota_finale}</p>
    </div>
  );
};
